import * as React from "react";
import { IProps, IRefHandler, useProps, useHandlers } from "./useHooks";
// components
import FileUploadManageDialog from "./components/fileUploadManageDialog/dialog";
import { FileAttachment } from "./styled";
import { VingleLoadable } from "app/vingle";
import { ThreadContentSkeleton } from "app/modules/forum/containers/forumShow/skeleton";

const ACCEPTABLE_IMAGE_MIME = [
  "image/gif",
  "image/png",
  "image/jpeg",
  "image/bmp",
  "image/webp",
  "image/avif",
  "image/tiff",
  "image/svg+xml",
  "image/heic",
  "image/heif",
];

const ACCEPTABLE_VIDEO_MIME = [
  "video/mp4",
  "video/quicktime",
  "video/webm",
  "audio/webm",
  "audio/ogg",
  "video/ogg",
  "audio/mp3",
  "audio/wav",
];

const QuillEditor = VingleLoadable(
  /* #__LOADABLE__ */ async () => import("./component"),
  {
    ssr: false,
  },
);

const RichEditor = React.forwardRef<IRefHandler, IProps>((props, ref) => {
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const {
    id,
    readonly,
    autofocus,
    placeholder,
    enableSingleLine,
    refQuillEditor,
    fileInputRef,
    imageFileInputRef,
    mediaFileInputRef,
    disableFileUploadShortcut,
    onceUploadLimitCount,
    contents,
    disableMentionPortal,
    disableEditor,
    disableMention,
    mentionPortalContainer,
    groupId,
    renderSkeleton,
    addFiles,
    addMediaFiles,
    addImageFiles,
    addImageBlock,
    containerStyle,
    isOpenFileManageDialog,
    closeFileManageDialog,
    handleSubmitFileManage,
    initialImageFiles,
    onBlur,
    onCancel,
    onChange,
    onChangeCursorFormat,
    getMoimDown,
    exportFileUpload,
    handleSaveSelection,
    handleFocus,
    handleSingleLineEnter,
    handleFileRetry,
    handleFileDelete,
    handleFileChange,
    handleImageFileChange,
    handleImageFileDelete,
    handleToolBoxLinkClick,
    handleInsertMention,
    handleSelectEmoji,
    handleEditorClear,
    handleEditorFocus,
    handleEditorBlur,
    handlePrependPreLinkMeeting,
    handleAddEmbedProduct,
    handleAddDownCoupon,
    getUploadQueue,
    handleUpdateTextFormat,
  } = useHandlers(useProps(props));

  const bindKeyBoard = React.useMemo(
    () => ({
      link: handleToolBoxLinkClick,
      mention: handleInsertMention,
      file: Boolean(disableFileUploadShortcut) ? undefined : addFiles,
    }),
    [
      addFiles,
      disableFileUploadShortcut,
      handleInsertMention,
      handleToolBoxLinkClick,
    ],
  );

  React.useImperativeHandle(ref, () => ({
    fileManagerOpenStatus: isOpenFileManageDialog,
    addFiles,
    addImageFiles,
    addMediaFiles,
    addImageBlock,
    addLink: handleToolBoxLinkClick,
    addMention: handleInsertMention,
    addEmoji: handleSelectEmoji,
    addEmbedProduct: handleAddEmbedProduct,
    addDownloadCoupon: handleAddDownCoupon,
    updateTextFormat: handleUpdateTextFormat,
    prependPreLinkMeeting: handlePrependPreLinkMeeting,
    getContent: getMoimDown,
    clear: handleEditorClear,
    focus: handleEditorFocus,
    blur: handleEditorBlur,
    fileUpload: exportFileUpload,
    onFileDelete: handleFileDelete,
    containerElement: refQuillEditor.current?.getQuill()?.root,
    getUploadQueue,
    setContent: refQuillEditor.current?.setContent,
  }));

  return (
    <>
      <QuillEditor
        key={`moim_editor_${id}`}
        id={id}
        ref={refQuillEditor}
        contents={contents}
        readonly={readonly}
        autofocus={autofocus}
        placeholder={placeholder}
        enableSingleLine={enableSingleLine}
        bindKeyboard={bindKeyBoard}
        containerStyle={containerStyle}
        disableMentionPortal={disableMentionPortal}
        mentionPortalContainer={mentionPortalContainer}
        groupId={groupId}
        renderSkeleton={renderSkeleton}
        disableEditor={disableEditor}
        disableMention={disableMention}
        onSingleLineEnter={handleSingleLineEnter}
        onFileUpload={exportFileUpload}
        onFileRetry={handleFileRetry}
        onFileDelete={handleFileDelete}
        onImageFileDelete={handleImageFileDelete}
        onFocus={handleFocus}
        onBlur={onBlur}
        onCancel={onCancel}
        onChange={onChange}
        onChangeCursorFormat={onChangeCursorFormat}
        onChangeSelection={handleSaveSelection}
        callbackMechanicalFocus={handleEditorFocus}
        fallback={enableSingleLine ? <></> : <ThreadContentSkeleton />}
      />
      <FileAttachment
        ref={fileInputRef}
        multiple={onceUploadLimitCount > 1}
        type="file"
        onChange={handleFileChange}
      />
      <FileAttachment
        ref={imageFileInputRef}
        multiple={onceUploadLimitCount > 1}
        type="file"
        accept={ACCEPTABLE_IMAGE_MIME.join(",")}
        onChange={handleImageFileChange}
      />
      <FileAttachment
        ref={mediaFileInputRef}
        multiple={onceUploadLimitCount > 1}
        type="file"
        accept={ACCEPTABLE_IMAGE_MIME.concat(ACCEPTABLE_VIDEO_MIME).join(",")}
        onChange={handleFileChange}
      />
      <FileUploadManageDialog
        initialFiles={initialImageFiles}
        open={isOpenFileManageDialog}
        onSubmit={handleSubmitFileManage}
        onClose={closeFileManageDialog}
      />
    </>
  );
});

// JPEG, PNG, WebP, AVIF, TIFF, GIF and SVG

export default React.memo(RichEditor);
export { IRefHandler };
